import React, { createElement, useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { CopyToClipboard } from 'react-copy-to-clipboard';

// Components
import WpLabelled from '../labelled/WpLabelled';
import WpButton from '../button/WpButton';
import WpIcon from '../icon/WpIcon';

// Styles
import './WpTextField.scss';

type Type = 'text' | 'password' | 'number' | 'hidden';

// currently only support text, more to come
// | 'email'
// | 'number'
// | 'password'
// | 'search';
export interface Validator {
  callback: Function;
  error: string;
}

export interface Props {
  /** Determine type of input */
  type?: Type;
  /** Allow for multiple lines of input */
  multiline?: boolean | number;
  /** ID for the input */
  id: string;
  /** Hint text to display */
  placeholder?: string;
  /** Initial value for the input */
  value?: string;
  /** Additional hint text to display */
  helpText?: React.ReactNode;
  /** Defines a specific role attribute for the input */
  role?: string;
  /** Label for the input */
  label: string;
  /** Hide the label, but keep on screen for screen readers */
  labelHidden?: boolean;
  /** Disable the input */
  disabled?: boolean;
  /** Error to display beneath the label */
  error?: string;
  /** Disable editing of the input */
  readOnly?: boolean;
  /** Automatically focus the input */
  autoFocus?: boolean;
  /** Callback when value is changed */
  onChange?(value: string): void;
  /** Callback when value is changed */
  onKeyDown?(event: React.ChangeEvent<HTMLInputElement>): void;
  /** Callback for onKeyPress */
  onKeyPress?(event: React.KeyboardEvent): void;
  /** Callback when input is focused */
  onFocus?(): void;
  /** Callback when focus is removed */
  onBlur?(): void;
  autoComplete?: boolean;
  required?: boolean;
  showRequiredIndicator?: boolean;
  className?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  enableCopy?: boolean;
  name?: string;
  hideBorder?: boolean;
  prefixIcon?: string;
  postfixIcon?: string;
  validate?: {
    [key: string]: Validator;
  };
  minLength?: number;
  min?: number;
  max?: number;
  qaTest?: string;
  onClear?: Function;
  onCopy?: Function;
  labelIcon?: string;
  labelTooltipText?: string;
  rightAlign?: boolean;
}

const WpTextField: React.FC<Props> = ({
  type,
  name,
  id,
  multiline,
  placeholder,
  value,
  readOnly,
  labelHidden,
  helpText,
  label,
  role,
  autoFocus,
  error,
  disabled,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onKeyPress,
  inputRef,
  enableCopy = false,
  hideBorder = false,
  prefixIcon,
  postfixIcon,
  required = false,
  min,
  max,
  showRequiredIndicator = true,
  autoComplete = false,
  qaTest,
  onClear,
  onCopy,
  labelIcon,
  labelTooltipText,
  rightAlign,
}: Props) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [textAreaHeight, setTextAreaHeight] = useState('auto');
  const [textAreaParentHeight, setTextAreaParentHeight] = useState('auto');
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (multiline) {
      setTextAreaParentHeight(`${textAreaRef.current!.scrollHeight + 2}px`);
      setTextAreaHeight(`${textAreaRef.current!.scrollHeight + 2}px`);
    }
  }, [value]);

  const mergeRefs = (...refs) => {
    const filteredRefs = refs.filter(Boolean);
    if (!filteredRefs.length) return null;
    if (filteredRefs.length === 0) return filteredRefs[0];
    return inst => {
      for (const ref of filteredRefs) {
        if (typeof ref === 'function') {
          ref(inst);
        } else if (ref) {
          ref.current = inst;
        }
      }
    };
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (multiline) {
      setTextAreaHeight(`auto`);
    }
    if (onChange) onChange(event.currentTarget.value);
  };

  const handleKeyDown = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onKeyDown) onKeyDown(event);
  };

  const handleOnKeyPress = (event: React.KeyboardEvent) => {
    if (onKeyPress) onKeyPress(event);
  };

  const handleOnblur = () => {
    if (onBlur) onBlur();
  };

  const handleOnClearClick = () => {
    if (onChange) onChange('');
    if (onClear) onClear();
  };

  const normalizeTextareaRow = (multiline?: boolean | number) => {
    if (!multiline) return undefined;
    return multiline === true ? 3 : multiline;
  };

  const normalizeAutoComplete = (autoComplete?: boolean | string) => {
    if (autoComplete === true) {
      return 'on';
    } else if (autoComplete === false) {
      return 'off';
    } else {
      return autoComplete;
    }
  };

  const baseClassName = 'WpTextField';
  const hasError = Boolean(error);

  const classes = classnames(baseClassName, type === 'hidden' ? `${baseClassName}--hidden` : false);

  const inputClassName = classnames(
    `${baseClassName}__input`,
    prefixIcon ? `${baseClassName}__input--has-prefix-icon` : false,
    postfixIcon ? `${baseClassName}__input--has-postfix-icon` : false,
    disabled ? `${baseClassName}__input--disabled` : false,
    hasError ? `${baseClassName}__input--has-error` : false,
    hideBorder ? `${baseClassName}__input--hideBorder` : false,
    rightAlign ? `${baseClassName}__input--right-align` : false
  );

  const input = createElement(multiline ? 'textarea' : 'input', {
    id,
    name,
    disabled,
    readOnly,
    role,
    autoFocus,
    value,
    placeholder,
    onFocus,
    onBlur: handleOnblur,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onKeyPress: handleOnKeyPress,
    type,
    className: inputClassName,
    ref: mergeRefs(textAreaRef, inputRef),
    rows: normalizeTextareaRow(multiline),
    style: multiline ? { height: textAreaHeight } : null,
    // /////////
    // TODO: Future supported props / functionality?
    // ////////
    // onKeyPress: handleKeyPress,
    autoComplete: normalizeAutoComplete(autoComplete),
    // ref: inputRef,
    min,
    max,
    minLength: min,
    maxLength: max,
    'data-test': qaTest,
    // focused,
    // step,
    // minLength,
    // maxLength,
    // spellCheck,
    // pattern,

    // /////////
    // TODO: look at aria attributes for accessbility
    // /////////
    // 'aria-describedby': describedBy.length ? describedBy.join(' ') : undefined,
    // 'aria-labelledby': labelledBy.join(' '),
    // 'aria-invalid': Boolean(error),
    // 'aria-owns': ariaOwns,
    // 'aria-activedescendant': ariaActiveDescendant,
    // 'aria-autocomplete': ariaAutocomplete,
    // 'aria-controls': ariaControls,
    // 'aria-multiline': normalizeAriaMultiline(multiline),
  });

  const onCopyClick = () => {
    setIsCopied(true);
    if (onCopy) onCopy();
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <div className={classes}>
      <WpLabelled
        label={label}
        helpText={helpText}
        error={error}
        id={id}
        labelHidden={labelHidden}
        showRequiredIndicator={required && showRequiredIndicator}
        labelIcon={labelIcon}
        labelTooltipText={labelTooltipText}
      >
        <div className={`${baseClassName}__wrapper`}>
          <div
            className={`${baseClassName}__input-wrapper`}
            style={multiline ? { height: textAreaParentHeight } : null}
          >
            {prefixIcon && (
              <div className={`${baseClassName}__input-wrapper__prefix-icon`}>
                <WpIcon name={prefixIcon} />
              </div>
            )}
            {input}
            {onClear && (
              <div className={`${baseClassName}__input-wrapper__postfix-icon`} onClick={e => e.stopPropagation()}>
                <WpButton size="sm" variant="tertiary" onClick={handleOnClearClick} rounded>
                  <WpIcon name="times-circle" />
                </WpButton>
              </div>
            )}
            {!onClear && postfixIcon && (
              <div className={`${baseClassName}__input-wrapper__postfix-icon`}>
                <WpIcon name={postfixIcon} />
              </div>
            )}
          </div>
          {enableCopy && (
            <span className="WpTextField__input-wrapper__copy-btn">
              <CopyToClipboard text={value} onCopy={onCopyClick}>
                <WpButton size="md" variant="tertiary" disabled={isCopied}>
                  <WpIcon name={isCopied ? 'check' : 'copy'} />
                  {isCopied ? 'copied!' : 'Copy'}
                </WpButton>
              </CopyToClipboard>
            </span>
          )}
        </div>
      </WpLabelled>
    </div>
  );
};

export default WpTextField;
